import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Header2 from "../components/Header2";
import Footer from "../components/Footer";
import Countdown from "../components/Countdown";
import ConnectModal from "../components/ConnectModal";
import AlertModal from "../components/AlertModal";
import { CONTRACTS, ABIS, CHAINS, DECIMALS } from "../config/contracts";
import { useAccount, useBalance, useReadContract, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from "wagmi";
import { Howl } from "howler";
import { formatEther, parseEther, parseUnits, formatUnits } from 'viem'

const Dashboard = () => {
  useEffect(() => {
    const sound = new Howl({ src: ["assets/sounds/open.wav"], volume: 0.6 });
    sound.play();
  }, [])
  
  const [input, setInput] = useState(0);
  const [selected, setSelected] = useState(1);
  const [openConnect, setOpenConnect] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyAndStakeLoading, setBuyAndStakeLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [pendingBuyAfterApproval, setPendingBuyAfterApproval] = useState(false);
  const [pendingBuyAndStakeAfterApproval, setPendingBuyAndStakeAfterApproval] = useState(false);
  const pendingBuyParamsRef = useRef(null);
  const pendingBuyAndStakeParamsRef = useRef(null);
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    title: "",
    message: "",
    actions: [],
  });

  const closeAlert = useCallback(() => {
    setAlertConfig((prev) => ({ ...prev, open: false, actions: [] }));
  }, []);

  const showAlert = useCallback((message, title = "Notice", actions = []) => {
    setAlertConfig({
      open: true,
      title,
      message,
      actions,
    });
  }, []);

  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient({ chainId: CHAINS.SEPOLIA });
  
  // ETH Balance
  const { data: ethData } = useBalance({
    address,
    chainId: CHAINS.SEPOLIA,
    watch: true,
    enabled: !!address,
  });

  //USDT Balance
  const { data: usdtData } = useReadContract({
    address: CONTRACTS.USDT,
    abi: ABIS.USDT,
    functionName: 'balanceOf',
    args: [address],
    chainId: CHAINS.ETHEREUM,
  })

  //AIFL Balance
  const { data: aiflData } = useReadContract({
    address: CONTRACTS.TOKEN,
    abi: ABIS.TOKEN,
    functionName: 'balanceOf',
    args: [address],
    chainId: CHAINS.SEPOLIA,
  })

// Presale: User Deposits (tokens purchased)
const { data: userDepositsData, refetch: refetchUserDeposits } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'userDeposits',
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
    watch: true,
})

// Staking: user staking data
const { data: userStakingData, refetch: refetchUserStaking } = useReadContract({
  address: CONTRACTS.STAKING,
  abi: ABIS.STAKING,
  functionName: 'poolStakers',
  args: [address],
  chainId: CHAINS.SEPOLIA,
  enabled: !!address,
  watch: true,
})

  // Presale: Total USD Raised
  const { data: usdRaisedData, refetch: refetchUsdRaised } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'usdRaised',
    chainId: CHAINS.SEPOLIA,
  })

  // Presale: Total Tokens Sold
  const { data: totalTokensSoldData, refetch: refetchTotalTokensSold } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'totalTokensSold',
    chainId: CHAINS.SEPOLIA,
  })
  
  // Presale: Current round info
  const { data: roundInfoData, refetch: refetchRoundInfo } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'getCurrentRoundInfo',
    chainId: CHAINS.SEPOLIA,
    watch: true,
  })

  // Presale: Total rounds
  const { data: totalRoundsData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'getTotalRounds',
    chainId: CHAINS.SEPOLIA,
    watch: true,
  })

  // Presale: Base decimals for price formatting
  const { data: baseDecimalsData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'baseDecimals',
    chainId: CHAINS.SEPOLIA,
  })

  // Presale: Claim start timestamp
  const { data: claimStartData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'claimStart',
    chainId: CHAINS.SEPOLIA,
  })

  // Presale: Has user claimed
  const { data: hasClaimedData, refetch: refetchHasClaimed } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'hasClaimed',
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
    watch: true,
  })

  // USDT Allowance for presale contract
  const { data: usdtAllowanceData, refetch: refetchUsdtAllowance } = useReadContract({
    address: CONTRACTS.USDT,
    abi: ABIS.USDT,
    functionName: 'allowance',
    args: [address, CONTRACTS.PRESALE],
    chainId: CHAINS.ETHEREUM,
    enabled: !!address,
  })

  // Get ETH price per token (for 1 token)
  const oneToken = parseUnits('1', DECIMALS.TOKEN) // 1 * 10^18
  const { data: ethPricePerTokenData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'ethBuyHelper',
    args: [oneToken],
    chainId: CHAINS.SEPOLIA,
  })

  // Get USDT/USD price per token (for 1 token)
  const { data: usdPricePerTokenData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: 'usdtBuyHelper',
    args: [oneToken],
    chainId: CHAINS.SEPOLIA,
  })

  // Write contracts
  const { writeContract: writeUsdtApprove, data: approveHash, error: writeApproveError, isError: isWriteApproveError, reset: resetApprove } = useWriteContract()
  const { writeContract: writeBuyWithEth, data: buyEthHash, error: writeBuyEthError, isError: isWriteBuyEthError, reset: resetBuyEth } = useWriteContract()
  const { writeContract: writeBuyWithUsdt, data: buyUsdtHash, error: writeBuyUsdtError, isError: isWriteBuyUsdtError, reset: resetBuyUsdt } = useWriteContract()
  const { writeContract: writeBuyWithEthAndStake, data: buyEthAndStakeHash, error: writeBuyEthAndStakeError, isError: isWriteBuyEthAndStakeError, reset: resetBuyEthAndStake } = useWriteContract()
  const { writeContract: writeBuyWithUsdtAndStake, data: buyUsdtAndStakeHash, error: writeBuyUsdtAndStakeError, isError: isWriteBuyUsdtAndStakeError, reset: resetBuyUsdtAndStake } = useWriteContract()
  const { writeContract: writeClaim, data: claimHash, error: writeClaimError, isError: isWriteClaimError, reset: resetClaim } = useWriteContract()

  // Transaction receipts
  const { isLoading: isApproving, isSuccess: isApproved, isError: isApprovalError, error: approvalError } = useWaitForTransactionReceipt({ hash: approveHash })
  const { isLoading: isBuying, isSuccess: isBought, isError: isBuyError, error: buyError } = useWaitForTransactionReceipt({ hash: buyEthHash || buyUsdtHash })
  const { isLoading: isBuyingAndStaking, isSuccess: isBoughtAndStaked, isError: isBuyAndStakeError, error: buyAndStakeError } = useWaitForTransactionReceipt({ hash: buyEthAndStakeHash || buyUsdtAndStakeHash })
  const { isLoading: isClaiming, isSuccess: isClaimed, isError: isClaimError, error: claimError } = useWaitForTransactionReceipt({ hash: claimHash })

  // Format balances
  const ethBalanceRaw = ethData?.value ?? 0n
  const usdtBalanceRaw = usdtData ?? 0n

  const ethBalance = isConnected && ethData
    ? Number(formatEther(ethData.value)).toFixed(4)
    : '0.0000'

  const usdtBalance = usdtData ? Number(formatUnits(usdtData, DECIMALS.USDT)).toFixed(2) : '0.00'
const aiflBalance = aiflData ? Number(formatUnits(aiflData, DECIMALS.TOKEN)).toFixed(2) : '0.00'
const userPurchasedNumeric = userDepositsData ? Number(formatUnits(userDepositsData, DECIMALS.TOKEN)) : 0
const userPurchased = userPurchasedNumeric.toFixed(2)
const userStakedNumeric = userStakingData ? Number(formatUnits(userStakingData[0] || 0n, DECIMALS.TOKEN)) : 0
const userStakedAmount = userStakedNumeric.toFixed(2)
// If user has claimed, stakeable amount should be 0
const userStakeableNumeric = hasClaimedData ? 0 : Math.max(0, userPurchasedNumeric - userStakedNumeric)
const userStakeableAmount = userStakeableNumeric.toFixed(2)
const totalRaised = usdRaisedData ? Number(formatUnits(usdRaisedData, DECIMALS.TOKEN)).toFixed(2) : '0'
  const totalSold = totalTokensSoldData ? Number(formatUnits(totalTokensSoldData, DECIMALS.TOKEN)).toFixed(0) : '0'
  const rawBaseDecimals = baseDecimalsData ? Number(baseDecimalsData) : DECIMALS.USDT
  const safeBaseDecimals = Number.isFinite(rawBaseDecimals)
    ? Math.min(Math.max(rawBaseDecimals, 0), 18)
    : DECIMALS.USDT

  // Extract round data from getCurrentRoundInfo
  // Returns: [roundNumber, roundPrice, timeUntilNext (seconds), isActive]
  const roundNumber = roundInfoData ? Number(roundInfoData[0]) : null
  const roundPriceRaw = roundInfoData ? roundInfoData[1] : null
  const timeUntilNext = roundInfoData ? Number(roundInfoData[2] || 0n) : 0 // seconds until next round
  const roundIsActive = roundInfoData ? Boolean(roundInfoData[3]) : false

  const totalRounds = totalRoundsData ? Number(totalRoundsData) : 0
  const isFinalRound = roundNumber !== null && totalRounds > 0 && roundNumber === totalRounds
  const isBetweenRounds = !roundIsActive && timeUntilNext > 0
  const isAfterFinalRound = isFinalRound && !roundIsActive && timeUntilNext === 0

const claimStartSeconds = claimStartData ? Number(claimStartData) : 0
const nowSeconds = Math.floor(Date.now() / 1000)
const isClaimWindowOpen = claimStartSeconds === 0 ? true : nowSeconds >= claimStartSeconds
const isClaimCountdown = (isAfterFinalRound || (!roundIsActive && timeUntilNext === 0)) && !isClaimWindowOpen && claimStartSeconds > 0

// When claim is active, use token balance instead of presale deposits for balance display
const balanceDisplay = isClaimWindowOpen ? aiflBalance : userStakeableAmount
  
  // Calculate countdown target: presale round or claim start
  const roundCountdownTarget = useMemo(() => {
    const currentTimestamp = Date.now()

    // If we're after the final round or in claim countdown, use claimStart
    if (isClaimCountdown && claimStartSeconds > 0) {
      const claimTimestamp = claimStartSeconds * 1000
      if (claimTimestamp > currentTimestamp) {
        const claimDate = new Date(claimTimestamp)
        if (!isNaN(claimDate.getTime())) {
          return claimDate.toISOString()
        }
      }
      return new Date(currentTimestamp - 1000).toISOString()
    }

    // If timeUntilNext is 0 and we're not in claim countdown, return past date
    if (timeUntilNext === 0 && !isClaimCountdown) {
      return new Date(currentTimestamp - 1000).toISOString()
    }
    
    if (timeUntilNext > 0 && Number.isFinite(timeUntilNext)) {
      const targetTimestamp = currentTimestamp + (timeUntilNext * 1000)
      
      const minTimestamp = Date.now() - (365 * 24 * 60 * 60 * 1000)
      const maxTimestamp = Date.now() + (10 * 365 * 24 * 60 * 60 * 1000)
      
      if (targetTimestamp >= minTimestamp && targetTimestamp <= maxTimestamp) {
        const date = new Date(targetTimestamp)
        if (!isNaN(date.getTime())) {
          return date.toISOString()
        }
      }
    }
    
    return new Date(currentTimestamp - 1000).toISOString()
  }, [timeUntilNext, isClaimCountdown, claimStartSeconds])

  // Determine countdown title based on round state
  let countdownTitle = 'Presale Ends In:'
  if (isClaimCountdown) {
    countdownTitle = 'Launch Starts In:'
  } else if (isFinalRound && roundIsActive) {
    countdownTitle = 'Presale Ends In:'
  } else if (isBetweenRounds) {
    countdownTitle = 'Ends In:'
  }
  
  let roundPrice = null
  if (roundPriceRaw) {
    try {
      roundPrice = Number(formatUnits(roundPriceRaw, safeBaseDecimals)).toFixed(6)
    } catch (error) {
      console.error('Failed to format round price', error)
      roundPrice = null
    }
  }

  const handleRoundCountdownComplete = useCallback(() => {
    console.log('Countdown completed! Refetching round data...')
    refetchRoundInfo()
    refetchTotalTokensSold()
    refetchUsdRaised()
    if (address) {
      refetchUserDeposits()
    }
  }, [refetchRoundInfo, refetchTotalTokensSold, refetchUsdRaised, refetchUserDeposits, address])

  // Debug: log round data to verify countdown calculation
  useEffect(() => {
    if (roundInfoData) {
      const currentTimestamp = Date.now()
      const targetTimestamp = currentTimestamp + (timeUntilNext * 1000)
      console.log('Presale roundInfoData:', roundInfoData)
      console.log('Countdown calculation →', {
        timeUntilNext: `${timeUntilNext} seconds (type: ${typeof timeUntilNext}, isFinite: ${Number.isFinite(timeUntilNext)})`,
        currentTimestamp: new Date(currentTimestamp).toISOString(),
        targetTimestamp: isNaN(targetTimestamp) ? 'INVALID' : new Date(targetTimestamp).toISOString(),
        roundCountdownTarget: roundCountdownTarget || 'NULL (invalid timestamp)',
        roundNumber,
        roundIsActive,
      })
    }
  }, [roundInfoData, timeUntilNext, roundCountdownTarget, roundNumber, roundIsActive])
  
  // Ensure wallet-dependent reads refetch immediately after connecting
  useEffect(() => {
    if (!address) return
    refetchUserDeposits()
    refetchHasClaimed()
    refetchUsdtAllowance()
    refetchUserStaking()
  }, [address, refetchUserDeposits, refetchHasClaimed, refetchUsdtAllowance, refetchUserStaking])

  useEffect(() => {
    if (!address) {
      return
    }
    const interval = setInterval(() => {
      refetchUserDeposits()
      refetchHasClaimed()
      refetchUsdtAllowance()
      refetchUserStaking()
      refetchRoundInfo()
      refetchUsdRaised()
      refetchTotalTokensSold()
    }, 15000)

    return () => clearInterval(interval)
  }, [
    address,
    refetchUserDeposits,
    refetchHasClaimed,
    refetchUsdtAllowance,
    refetchUserStaking,
    refetchRoundInfo,
    refetchUsdRaised,
    refetchTotalTokensSold,
  ])

  const isUserRejected = useCallback((error) => {
    const message = (error?.shortMessage || error?.message || '').toLowerCase()
    return error?.name === 'UserRejectedRequestError' || error?.code === 4001 || message.includes('user rejected') || message.includes('user canceled') || message.includes('user cancelled')
  }, [])

  // Handle writeContract errors (immediate user cancellations in MetaMask)
  useEffect(() => {
    if (isWriteApproveError && writeApproveError) {
      const wasPending = pendingBuyAfterApproval || pendingBuyAndStakeAfterApproval
      setApprovalLoading(false)
      setPendingBuyAfterApproval(false)
      setPendingBuyAndStakeAfterApproval(false)
      pendingBuyParamsRef.current = null
      pendingBuyAndStakeParamsRef.current = null
      if (isUserRejected(writeApproveError)) {
        showAlert(wasPending ? 'Approval canceled.' : 'Approval canceled.', 'Action Canceled')
      }
      resetApprove()
    }
  }, [isWriteApproveError, writeApproveError, pendingBuyAfterApproval, pendingBuyAndStakeAfterApproval, showAlert, isUserRejected, resetApprove])

  useEffect(() => {
    if (isWriteBuyEthError && writeBuyEthError) {
      setBuyLoading(false)
      setPendingBuyAfterApproval(false)
      pendingBuyParamsRef.current = null
      if (isUserRejected(writeBuyEthError)) {
        showAlert('Buy canceled.', 'Action Canceled')
      }
      resetBuyEth()
    }
  }, [isWriteBuyEthError, writeBuyEthError, showAlert, isUserRejected, resetBuyEth])

  useEffect(() => {
    if (isWriteBuyUsdtError && writeBuyUsdtError) {
      setBuyLoading(false)
      setPendingBuyAfterApproval(false)
      pendingBuyParamsRef.current = null
      if (isUserRejected(writeBuyUsdtError)) {
        showAlert('Buy canceled.', 'Action Canceled')
      }
      resetBuyUsdt()
    }
  }, [isWriteBuyUsdtError, writeBuyUsdtError, showAlert, isUserRejected, resetBuyUsdt])

  useEffect(() => {
    if (isWriteBuyEthAndStakeError && writeBuyEthAndStakeError) {
      setBuyAndStakeLoading(false)
      setPendingBuyAndStakeAfterApproval(false)
      pendingBuyAndStakeParamsRef.current = null
      if (isUserRejected(writeBuyEthAndStakeError)) {
        showAlert('Buy and Stake canceled.', 'Action Canceled')
      }
      resetBuyEthAndStake()
    }
  }, [isWriteBuyEthAndStakeError, writeBuyEthAndStakeError, showAlert, isUserRejected, resetBuyEthAndStake])

  useEffect(() => {
    if (isWriteBuyUsdtAndStakeError && writeBuyUsdtAndStakeError) {
      setBuyAndStakeLoading(false)
      setPendingBuyAndStakeAfterApproval(false)
      pendingBuyAndStakeParamsRef.current = null
      if (isUserRejected(writeBuyUsdtAndStakeError)) {
        showAlert('Buy and Stake canceled.', 'Action Canceled')
      }
      resetBuyUsdtAndStake()
    }
  }, [isWriteBuyUsdtAndStakeError, writeBuyUsdtAndStakeError, showAlert, isUserRejected, resetBuyUsdtAndStake])

  useEffect(() => {
    if (isWriteClaimError && writeClaimError) {
      setClaimLoading(false)
      if (isUserRejected(writeClaimError)) {
        showAlert('Claim canceled.', 'Action Canceled')
      }
      resetClaim()
    }
  }, [isWriteClaimError, writeClaimError, showAlert, isUserRejected, resetClaim])

  // Handle transaction receipt errors (after transaction is sent)
  useEffect(() => {
    if (isApprovalError && approvalError) {
      const wasPending = pendingBuyAfterApproval || pendingBuyAndStakeAfterApproval
      setApprovalLoading(false)
      setPendingBuyAfterApproval(false)
      setPendingBuyAndStakeAfterApproval(false)
      pendingBuyParamsRef.current = null
      pendingBuyAndStakeParamsRef.current = null
      if (isUserRejected(approvalError)) {
        showAlert(wasPending ? 'Approval canceled.' : 'Approval canceled.', 'Action Canceled')
      }
    }
  }, [isApprovalError, approvalError, pendingBuyAfterApproval, pendingBuyAndStakeAfterApproval, showAlert, isUserRejected])

  useEffect(() => {
    if (isBuyError && buyError) {
      setBuyLoading(false)
      setPendingBuyAfterApproval(false)
      pendingBuyParamsRef.current = null
      if (isUserRejected(buyError)) {
        showAlert('Buy canceled.', 'Action Canceled')
      }
    }
  }, [isBuyError, buyError, showAlert, isUserRejected])

  useEffect(() => {
    if (isBuyAndStakeError && buyAndStakeError) {
      setBuyAndStakeLoading(false)
      setPendingBuyAndStakeAfterApproval(false)
      pendingBuyAndStakeParamsRef.current = null
      if (isUserRejected(buyAndStakeError)) {
        showAlert('Buy and Stake canceled.', 'Action Canceled')
      }
    }
  }, [isBuyAndStakeError, buyAndStakeError, showAlert, isUserRejected])

  useEffect(() => {
    if (isClaimError && claimError) {
      setClaimLoading(false)
      if (isUserRejected(claimError)) {
        showAlert('Claim canceled.', 'Action Canceled')
      }
    }
  }, [isClaimError, claimError, showAlert, isUserRejected])

  // Refetch data after transactions
  useEffect(() => {
    if (isApproved) {
      refetchUsdtAllowance()
      setApprovalLoading(false)
      
      // If we were waiting to buy after approval, proceed with buy
      if (pendingBuyAfterApproval && pendingBuyParamsRef.current) {
        const params = pendingBuyParamsRef.current
        if (params.paymentMethod === 'usdt') {
          writeBuyWithUsdt({
            address: CONTRACTS.PRESALE,
            abi: ABIS.PRESALE,
            functionName: 'buyWithUSDT',
            args: [params.tokensInWei, params.stake],
            chainId: CHAINS.SEPOLIA,
          })
        }
        setPendingBuyAfterApproval(false)
        pendingBuyParamsRef.current = null
      } else if (pendingBuyAndStakeAfterApproval && pendingBuyAndStakeParamsRef.current) {
        // If we were waiting to buy and stake after approval, proceed with buy and stake
        const params = pendingBuyAndStakeParamsRef.current
        if (params.paymentMethod === 'usdt') {
          writeBuyWithUsdtAndStake({
            address: CONTRACTS.PRESALE,
            abi: ABIS.PRESALE,
            functionName: 'buyWithUSDT',
            args: [params.tokensInWei, params.stake],
            chainId: CHAINS.SEPOLIA,
          })
        }
        setPendingBuyAndStakeAfterApproval(false)
        pendingBuyAndStakeParamsRef.current = null
      } else if (!pendingBuyAfterApproval && !pendingBuyAndStakeAfterApproval) {
        // Only show approval message if it wasn't part of a buy flow
        showAlert('USDT Approved! You can now buy AIFL.', 'Success')
      }
    }
  }, [isApproved, refetchUsdtAllowance, showAlert, pendingBuyAfterApproval, pendingBuyAndStakeAfterApproval, writeBuyWithUsdt, writeBuyWithUsdtAndStake])

  useEffect(() => {
    if (isBought) {
      refetchUserDeposits()
      setBuyLoading(false)
      setPendingBuyAfterApproval(false)
      pendingBuyParamsRef.current = null
      showAlert('Purchase successful! Tokens added to your account.', 'Success')
    }
  }, [isBought, refetchUserDeposits, showAlert])

useEffect(() => {
  if (isBoughtAndStaked) {
    refetchUserDeposits()
    refetchUserStaking()
    setBuyAndStakeLoading(false)
    setPendingBuyAndStakeAfterApproval(false)
    pendingBuyAndStakeParamsRef.current = null
      showAlert('Purchase and staking successful! Tokens have been staked.', 'Success')
  }
  }, [isBoughtAndStaked, refetchUserDeposits, refetchUserStaking, showAlert])

  useEffect(() => {
    if (isClaimed) {
      refetchUserDeposits()
      refetchHasClaimed()
      setClaimLoading(false)
      showAlert('Claim successful! Tokens transferred to your wallet.', 'Success')
    }
  }, [isClaimed, refetchUserDeposits, refetchHasClaimed, showAlert])

  const notifyTxError = useCallback((error, fallbackMessage, action = 'Action') => {
    if (isUserRejected(error)) {
      showAlert(`${action} canceled.`, 'Action Canceled')
    } else {
      showAlert(fallbackMessage, 'Error')
    }
  }, [showAlert, isUserRejected])

  // Handler functions
  const handleApproveUsdt = async () => {
    if (!isConnected) {
      setOpenConnect(true)
      return
    }
    try {
      setApprovalLoading(true)
      const maxApproval = parseUnits('1000000', DECIMALS.USDT) // Approve 1M USDT
      writeUsdtApprove({
        address: CONTRACTS.USDT,
        abi: ABIS.USDT,
        functionName: 'approve',
        args: [CONTRACTS.PRESALE, maxApproval],
        chainId: CHAINS.ETHEREUM,
      })
    } catch (error) {
      console.error('Approve error:', error)
      setApprovalLoading(false)
      notifyTxError(error, 'Failed to approve USDT', 'Approval')
    }
  }

  const handleBuy = async () => {
    if (!isConnected) {
      setOpenConnect(true)
      return
    }
    
    if (!input || input <= 0) {
      showAlert('Please enter a valid amount', 'Validation')
      return
    }

    try {
      setBuyLoading(true)
      
      if (!publicClient) {
        showAlert('Unable to connect to blockchain. Please try again.', 'Network Error')
        setBuyLoading(false)
        return
      }
      
      if (selected == 1) {
        // Buy with ETH
        const userEthInput = parseEther(input.toString())
        if (userEthInput > ethBalanceRaw) {
          showAlert('Insufficient ETH balance', 'Validation')
          setBuyLoading(false)
          return
        }
        
        // Step 1: Get ETH price for 1 token from contract
        const oneToken = parseUnits('1', DECIMALS.TOKEN) // 1 * 10^18
        const ethPricePerToken = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'ethBuyHelper',
          args: [oneToken],
        })
        
        if (!ethPricePerToken || ethPricePerToken === 0n) {
          showAlert('Failed to get token price from contract', 'Error')
          setBuyLoading(false)
          return
        }
        
        // Step 2: Calculate how many tokens user gets for their ETH input
        // userEthInput (wei) / ethPricePerToken (wei per token) = tokens
        const tokensInWei = (userEthInput * oneToken) / ethPricePerToken
        
        if (tokensInWei === 0n) {
          showAlert('ETH amount too small to purchase any tokens', 'Validation')
          setBuyLoading(false)
          return
        }
        
        // Step 3: Get exact ETH amount needed for calculated tokens
        const requiredEthAmount = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'ethBuyHelper',
          args: [tokensInWei],
        })
        
        if (!requiredEthAmount) {
          showAlert('Failed to calculate required ETH amount', 'Error')
          setBuyLoading(false)
          return
        }
        
        // Check if user has enough for the calculated amount
        if (requiredEthAmount > ethBalanceRaw) {
          showAlert(`Insufficient ETH balance. Required: ${formatEther(requiredEthAmount)} ETH`, 'Validation')
          setBuyLoading(false)
          return
        }
        
        writeBuyWithEth({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'buyWithEth',
          args: [tokensInWei, false], // AIFL token amount, stake (false for now)
          value: requiredEthAmount, // Send exact ETH amount contract expects
          chainId: CHAINS.SEPOLIA,
        })
      } else if (selected == 2 || selected == 3) {
        // Buy with USDT
        const userUsdtInput = parseUnits(input.toString(), DECIMALS.USDT)
        if (userUsdtInput > usdtBalanceRaw) {
          showAlert('Insufficient USDT balance', 'Validation')
          setBuyLoading(false)
          return
        }
        
        // Step 1: Get USD price for 1 token from contract
        const oneToken = parseUnits('1', DECIMALS.TOKEN) // 1 * 10^18
        const usdPricePerToken = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'usdtBuyHelper',
          args: [oneToken],
        })
        
        if (!usdPricePerToken || usdPricePerToken === 0n) {
          showAlert('Failed to get token price from contract', 'Error')
          setBuyLoading(false)
          return
        }
        
        // Step 2: Calculate how many tokens user gets for their USDT input
        // userUsdtInput (6 decimals) needs to be converted to 18 decimals for calculation
        // userUsdtInput * 10^12 (to get to 18 decimals) / usdPricePerToken = tokens
        const usdtIn18Decimals = userUsdtInput * parseUnits('1', 12) // Convert 6 decimals to 18
        const tokensInWei = (usdtIn18Decimals * oneToken) / usdPricePerToken
        
        if (tokensInWei === 0n) {
          showAlert('USDT amount too small to purchase any tokens', 'Validation')
          setBuyLoading(false)
          return
        }
        
        // Step 3: Get exact USDT amount needed for calculated tokens
        const requiredUsdtAmount = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'usdtBuyHelper',
          args: [tokensInWei],
        })
        
        if (!requiredUsdtAmount) {
          showAlert('Failed to calculate required USDT amount', 'Error')
          setBuyLoading(false)
          return
        }
        
        // Check if user has enough for the calculated amount (convert back to 6 decimals)
        const requiredUsdt6Dec = requiredUsdtAmount / parseUnits('1', 12)
        if (requiredUsdt6Dec > usdtBalanceRaw) {
          showAlert(`Insufficient USDT balance. Required: ${formatUnits(requiredUsdt6Dec, DECIMALS.USDT)} USDT`, 'Validation')
          setBuyLoading(false)
          return
        }
        
        // Check if approval is needed
        if (!usdtAllowanceData || usdtAllowanceData < requiredUsdt6Dec) {
          // Need to approve first, then buy
          setPendingBuyAfterApproval(true)
          pendingBuyParamsRef.current = {
            paymentMethod: 'usdt',
            tokensInWei,
            stake: false,
          }
          setApprovalLoading(true)
          const maxApproval = parseUnits('1000000', DECIMALS.USDT) // Approve 1M USDT
          writeUsdtApprove({
            address: CONTRACTS.USDT,
            abi: ABIS.USDT,
            functionName: 'approve',
            args: [CONTRACTS.PRESALE, maxApproval],
            chainId: CHAINS.ETHEREUM,
          })
          return
        }
        
        // Approval is sufficient, proceed with buy
        writeBuyWithUsdt({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'buyWithUSDT',
          args: [tokensInWei, false], // AIFL token amount, stake
          chainId: CHAINS.SEPOLIA,
        })
      }
    } catch (error) {
      console.error('Buy error:', error)
      const wasApproving = approvalLoading || pendingBuyAfterApproval
      setBuyLoading(false)
      setApprovalLoading(false)
      setPendingBuyAfterApproval(false)
      pendingBuyParamsRef.current = null
      notifyTxError(error, 'Failed to buy tokens', wasApproving ? 'Approval' : 'Buy')
    }
  }

  const handleBuyAndStake = async () => {
    if (!isConnected) {
      setOpenConnect(true)
      return
    }
    
    if (!input || input <= 0) {
      showAlert('Please enter a valid amount', 'Validation')
      return
    }

    try {
      setBuyAndStakeLoading(true)
      
      if (!publicClient) {
        showAlert('Unable to connect to blockchain. Please try again.', 'Network Error')
        setBuyAndStakeLoading(false)
        return
      }
      
      if (selected == 1) {
        // Buy with ETH and stake
        const userEthInput = parseEther(input.toString())
        if (userEthInput > ethBalanceRaw) {
          showAlert('Insufficient ETH balance', 'Validation')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Step 1: Get ETH price for 1 token from contract
        const oneToken = parseUnits('1', DECIMALS.TOKEN) // 1 * 10^18
        const ethPricePerToken = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'ethBuyHelper',
          args: [oneToken],
        })
        
        if (!ethPricePerToken || ethPricePerToken === 0n) {
          showAlert('Failed to get token price from contract', 'Error')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Step 2: Calculate how many tokens user gets for their ETH input
        const tokensInWei = (userEthInput * oneToken) / ethPricePerToken
        
        if (tokensInWei === 0n) {
          showAlert('ETH amount too small to purchase any tokens', 'Validation')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Step 3: Get exact ETH amount needed for calculated tokens
        const requiredEthAmount = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'ethBuyHelper',
          args: [tokensInWei],
        })
        
        if (!requiredEthAmount) {
          showAlert('Failed to calculate required ETH amount', 'Error')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Check if user has enough for the calculated amount
        if (requiredEthAmount > ethBalanceRaw) {
          showAlert(`Insufficient ETH balance. Required: ${formatEther(requiredEthAmount)} ETH`, 'Validation')
          setBuyAndStakeLoading(false)
          return
        }
        
        writeBuyWithEthAndStake({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'buyWithEth',
          args: [tokensInWei, true], // AIFL token amount, stake = true
          value: requiredEthAmount, // Send exact ETH amount contract expects
          chainId: CHAINS.SEPOLIA,
        })
      } else if (selected == 2 || selected == 3) {
        // Buy with USDT and stake
        const userUsdtInput = parseUnits(input.toString(), DECIMALS.USDT)
        if (userUsdtInput > usdtBalanceRaw) {
          showAlert('Insufficient USDT balance', 'Validation')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Step 1: Get USD price for 1 token from contract
        const oneToken = parseUnits('1', DECIMALS.TOKEN) // 1 * 10^18
        const usdPricePerToken = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'usdtBuyHelper',
          args: [oneToken],
        })
        
        if (!usdPricePerToken || usdPricePerToken === 0n) {
          showAlert('Failed to get token price from contract', 'Error')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Step 2: Calculate how many tokens user gets for their USDT input
        const usdtIn18Decimals = userUsdtInput * parseUnits('1', 12) // Convert 6 decimals to 18
        const tokensInWei = (usdtIn18Decimals * oneToken) / usdPricePerToken
        
        if (tokensInWei === 0n) {
          showAlert('USDT amount too small to purchase any tokens', 'Validation')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Step 3: Get exact USDT amount needed for calculated tokens
        const requiredUsdtAmount = await publicClient.readContract({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'usdtBuyHelper',
          args: [tokensInWei],
        })
        
        if (!requiredUsdtAmount) {
          showAlert('Failed to calculate required USDT amount', 'Error')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Check if user has enough for the calculated amount (convert back to 6 decimals)
        const requiredUsdt6Dec = requiredUsdtAmount / parseUnits('1', 12)
        if (requiredUsdt6Dec > usdtBalanceRaw) {
          showAlert(`Insufficient USDT balance. Required: ${formatUnits(requiredUsdt6Dec, DECIMALS.USDT)} USDT`, 'Validation')
          setBuyAndStakeLoading(false)
          return
        }
        
        // Check if approval is needed
        if (!usdtAllowanceData || usdtAllowanceData < requiredUsdt6Dec) {
          // Need to approve first, then buy and stake
          setPendingBuyAndStakeAfterApproval(true)
          pendingBuyAndStakeParamsRef.current = {
            paymentMethod: 'usdt',
            tokensInWei,
            stake: true,
          }
          setApprovalLoading(true)
          const maxApproval = parseUnits('1000000', DECIMALS.USDT) // Approve 1M USDT
          writeUsdtApprove({
            address: CONTRACTS.USDT,
            abi: ABIS.USDT,
            functionName: 'approve',
            args: [CONTRACTS.PRESALE, maxApproval],
            chainId: CHAINS.ETHEREUM,
          })
          return
        }
        
        // Approval is sufficient, proceed with buy and stake
        writeBuyWithUsdtAndStake({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: 'buyWithUSDT',
          args: [tokensInWei, true], // AIFL token amount, stake = true
          chainId: CHAINS.SEPOLIA,
        })
      }
    } catch (error) {
      console.error('Buy and Stake error:', error)
      const wasApproving = approvalLoading || pendingBuyAndStakeAfterApproval
      setBuyAndStakeLoading(false)
      setApprovalLoading(false)
      setPendingBuyAndStakeAfterApproval(false)
      pendingBuyAndStakeParamsRef.current = null
      notifyTxError(error, 'Failed to buy and stake tokens', wasApproving ? 'Approval' : 'Buy and Stake')
    }
  }

  const handleClaim = async () => {
    if (!isConnected) {
      setOpenConnect(true)
      return
    }

    if (!isClaimWindowOpen) {
      showAlert('Claiming is not live yet. Please wait until the claim start time.', 'Claim Locked')
      return
    }
    
    if (!userDepositsData || userDepositsData === 0n) {
      showAlert('No tokens to claim', 'Validation')
      return
    }
    
    if (hasClaimedData) {
      showAlert('You have already claimed your tokens', 'Notice')
      return
    }

    try {
      setClaimLoading(true)
      writeClaim({
        address: CONTRACTS.PRESALE,
        abi: ABIS.PRESALE,
        functionName: 'claim',
        chainId: CHAINS.SEPOLIA,
      })
    } catch (error) {
      console.error('Claim error:', error)
      setClaimLoading(false)
      notifyTxError(error, 'Failed to claim tokens', 'Claim')
    }
  }

  const purchaseButtons = [
    {
      id: "1",
      text: "ETH",
      reverse: "reverse",
    },
    {
      id: "2",
      text: "USDT",
      reverse: "",
    },
    {
      id: "3",
      text: "CARD",
      reverse: "reverse",
    },
  ];
  
  return (
    <>
      <AlertModal
        isOpen={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        actions={alertConfig.actions}
        onClose={closeAlert}
      />
      <Header2 openModal={() => {setOpenConnect(true)}} />
      <main className="main">
        <div className="staking" style={{marginBottom: "100px"}}>
          {
            openConnect ? <ConnectModal closeModal={() => setOpenConnect(false)}/> : ""
          }
          <div className="wrapper">
            <div className="staking-top dash-order">
              <div
                className="staking-left2 dash-order1"
                style={{ opacity: "1" }}
              >
                <div className="wrapper">
                  <div className="staking-bottom-content">
                    <h3>Understanding the AIFL Service and Staking Benefits</h3>
                    <div className="intro">
                      <p>
                        The AIFlow service incorporates a burn mechanism to
                        reduce the total supply of AIFL tokens, achieved by
                        permanently removing a portion of tokens from
                        circulation. This scarcity can potentially increase the
                        value of remaining tokens. Our automated process ensures
                        regular, consistent impact on the token's supply and
                        value.
                      </p>
                    </div>
                    <div className="p">
                      <div>
                        Here are some key features of our Staking service:
                        <ul>
                          <li>
                            Access to AIFL for querying specific data from our
                            database dataset.
                          </li>
                          <li>
                            Sentiment Analysis based on community member
                            engagement and trustability scores.
                          </li>
                          <li>
                            Detailed project insights, including direct pros and
                            cons assessed by various AIs, with summarized
                            information accessible on the web application.
                          </li>
                          <li>
                            Capability to request specialized analysis of any
                            project by providing its website link, utilizing our
                            LLM AI system for real-time evaluation and rating.
                          </li>
                          <li>
                            Customizable alerts based on pre-set criteria in our
                            application, enabling users to receive timely
                            information about new launches that meet their
                            conditions via socials, emails, etc.
                          </li>
                          <li>
                            Exclusive real-time news and analysis updates from
                            our system, delivered directly without delay.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="staking-right dash-order2"
                style={{
                  translate: "none",
                  rotate: "none",
                  scale: "none",
                  transform: "translate(0px)",
                  opacity: "1",
                }}
              >
                <div className="card total">
                  <div className="total-heading">
                    <div className="total-heading-left">
                      <div className="sub">
                        {roundIsActive && roundNumber !== null ? `ROUND ${roundNumber}` : "ROUND STATUS"}
                      </div>
                      <h5>{countdownTitle}</h5>
                    </div>
                  </div>

                  {roundCountdownTarget ? (
                    <Countdown targetDate={roundCountdownTarget} onComplete={handleRoundCountdownComplete} />
                  ) : (
                    <div className="total-input-content" style={{ textAlign: "center", padding: "1.5rem 0" }}>
                      <strong>{roundIsActive ? "Calculating round timer..." : "No active round"}</strong>
                    </div>
                  )}

                  <div className="total-input">
                    RAISED
                    <div className="total-input-content" style={{ fontSize: "clamp(0.8em, 1vw, 1.5em)"}}>
                      <strong>${totalRaised} / $9,200,750</strong>
                    </div>
                    <div className="corner reverse" data-colors="tertiary">
                      <span className="corner-left"></span>
                      <span className="corner-right"></span>
                      <span className="corner-top-bottom"></span>
                    </div>
                  </div>
                  {!isClaimWindowOpen && (
                    <>
                      <div className="total-input">
                        1 AIFL
                        <div className="total-input-content">
                          <strong>
                            {roundPrice ? ` = $${roundPrice}` : " $0"}
                          </strong>
                        </div>
                        <div className="corner reverse" data-colors="tertiary">
                          <span className="corner-left"></span>
                          <span className="corner-right"></span>
                          <span className="corner-top-bottom"></span>
                        </div>
                      </div>
                      <div>
                        <div className="total-heading-left"></div>
                        <div
                          style={{
                            display: "flex",
                            gap: "16px",
                            marginBottom: "20px",
                          }}
                        >
                          {purchaseButtons.map((btn) => {
                            return (
                              <button
                                className={
                                  selected == btn.id ? "btn selected" : "btn"
                                }
                                data-colors="primary"
                                style={{ flex: "1" }}
                                key={btn.id}
                                onClick={() => setSelected(btn.id)}
                              >
                                <span className="btn-txt">{btn.text}</span>
                                <div
                                  className={"corner " + btn.reverse}
                                  data-colors="primary"
                                >
                                  <span className="corner-left"></span>
                                  <span className="corner-right"></span>
                                  <span className="corner-top-bottom"></span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "16px",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              flex: "1",
                            }}
                          >
                            <p style={{ marginBottom: "12px", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                              <span>
                              Pay
                              {selected == 1
                                ? " ETH"
                                : selected == 2
                                ? " USDT"
                                : " with CARD"}
                              </span>
                              <span>
                              
                              {selected == 1
                                ? ` ${ethBalance}`
                                : selected == 2
                                ? ` ${usdtBalance}`
                                : ""}
                              </span>
                            </p>
                            <div className="total-input">
                              {selected == 1
                                ? "ETH"
                                : selected == 2
                                ? "USDT"
                                : "USD"}
                              <input
                                className="pay-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                type="number"
                                style={{ fontSize: "clamp(1em, 2vw, 1.5em)"}}
                              />
                              <div className="corner" data-colors="tertiary">
                                <span className="corner-left"></span>
                                <span className="corner-right"></span>
                                <span className="corner-top-bottom"></span>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              flex: "1",
                            }}
                          >
                            <p style={{ marginBottom: "12px", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                              <span>
                              Balance:
                              </span>
                              <span>
                            {balanceDisplay}
                              </span>
                            </p>
                            <div className="total-input">
                              AIFL
                              <div className="total-input-content" style={{ fontSize: "clamp(0.8em, 1vw, 1.5em)"}}>
                                <div 
                                  style={{ 
                                    overflowX: "auto", 
                                    overflowY: "hidden",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    flex: "1 1 0",
                                    display: "flex",
                                    alignItems: "center",
                                    scrollbarWidth: "thin",
                                    WebkitOverflowScrolling: "touch",
                                    position: "relative"
                                  }}
                                >
                                  <strong style={{ 
                                    display: "inline-block", 
                                    whiteSpace: "nowrap",
                                    overflow: "visible"
                                  }}>
                                    {
                                      (() => {
                                        if (!input || input <= 0) return 0;
                                        
                                        if (selected == 1) {
                                          // ETH: use contract's ethBuyHelper price
                                          if (!ethPricePerTokenData || ethPricePerTokenData === 0n) return 0;
                                          const userEthInWei = parseEther(input.toString());
                                          const tokensInWei = (userEthInWei * oneToken) / ethPricePerTokenData;
                                          return Math.trunc(Number(formatUnits(tokensInWei, DECIMALS.TOKEN)));
                                        } else if (selected == 2 || selected == 3) {
                                          // USDT: use contract's usdtBuyHelper price
                                          if (!usdPricePerTokenData || usdPricePerTokenData === 0n) return 0;
                                          const userUsdtInWei = parseUnits(input.toString(), DECIMALS.USDT);
                                          const usdtIn18Decimals = userUsdtInWei * parseUnits('1', 12); // Convert 6 to 18 decimals
                                          const tokensInWei = (usdtIn18Decimals * oneToken) / usdPricePerTokenData;
                                          return Math.trunc(Number(formatUnits(tokensInWei, DECIMALS.TOKEN)));
                                        }
                                        
                                        return 0;
                                      })()
                                    }
                                  </strong>
                                </div>
                              </div>
                              <div
                                className="corner reverse"
                                data-colors="tertiary"
                              >
                                <span className="corner-left"></span>
                                <span className="corner-right"></span>
                                <span className="corner-top-bottom"></span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <br />
                        <div style={{ display: "flex", gap: "24px", flex: "1" }}>
                          <button
                            className="btn"
                            data-colors="primary"
                            style={{ flex: "1", fontSize: "clamp(0.8em, 1vw, 1.5em)" }}
                            onClick={isConnected ? handleBuy : () => setOpenConnect(true)}
                            disabled={buyLoading || isBuying || approvalLoading || isApproving}
                          >
                            <span className="btn-txt">
                              {!isConnected ? "Connect Wallet" : approvalLoading || isApproving ? "Approving..." : buyLoading || isBuying ? "Buying..." : "Buy AIFL"}
                            </span>
                            <div className="corner reverse" data-colors="primary">
                              <span className="corner-left"></span>
                              <span className="corner-right"></span>
                              <span className="corner-top-bottom"></span>
                            </div>
                          </button>
                          <button
                            className="btn"
                            data-colors="primary"
                            style={{ flex: "1", fontSize: "clamp(0.8em, 1vw, 1.5em)" }}
                            onClick={isConnected ? handleBuyAndStake : () => setOpenConnect(true)}
                            disabled={buyAndStakeLoading || isBuyingAndStaking || approvalLoading || isApproving}
                          >
                            <span className="btn-txt">{!isConnected ? "Connect Wallet" : approvalLoading || isApproving ? "Approving..." : buyAndStakeLoading || isBuyingAndStaking ? "Buying and Staking..." : "Buy and Stake"}</span>
                            <div className="corner" data-colors="primary">
                              <span className="corner-left"></span>
                              <span className="corner-right"></span>
                              <span className="corner-top-bottom"></span>
                            </div>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                    <div className="corner reverse" data-colors="tertiary">
                      <span className="corner-left"></span>
                      <span className="corner-right"></span>
                      <span className="corner-top-bottom"></span>
                    </div>
                    {isClaimWindowOpen && (
                      <div className="unlock-title">
                        <span>You can now claim your purchased AIFL below.</span>
                      </div>
                    )}
                </div>
                <div className="staking-separator" data-status="warning">
                  <div className="cube">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      aria-hidden="true"
                      role="img"
                      className="iconify iconify--carbon"
                      width="1em"
                      height="1em"
                      viewBox="0 0 32 32"
                    >
                      <path
                        fill="currentColor"
                        d="M24 14h-2V8a6 6 0 0 0-12 0v6H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V16a2 2 0 0 0-2-2M12 8a4 4 0 0 1 8 0v6h-8Zm12 20H8V16h16Z"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div className="card unlock">
                  <div className="unlock-title">
                    <h5>Presale Event Details:</h5>
                  </div>
                  <div className="total-input">
                    Your Purchased
                    <div className="total-input-content">
                      <strong>{userPurchased} AIFL</strong>
                    </div>
                    <div className="corner" data-colors="tertiary">
                      <span className="corner-left"></span>
                      <span className="corner-right"></span>
                      <span className="corner-top-bottom"></span>
                    </div>
                  </div>
                  <div className="total-input">
                    Your Stakeable
                    <div className="total-input-content">
                      <strong>{userStakeableAmount} AIFL</strong>
                    </div>
                    <div className="corner" data-colors="tertiary">
                      <span className="corner-left"></span>
                      <span className="corner-right"></span>
                      <span className="corner-top-bottom"></span>
                    </div>
                  </div>
                  <button 
                    className="btn" 
                    data-colors="primary"
                    onClick={handleClaim}
                    disabled={claimLoading || isClaiming || !isConnected || hasClaimedData || !isClaimWindowOpen}
                  >
                    <span className="btn-txt">
                      {hasClaimedData
                        ? "Already Claimed"
                        : !isClaimWindowOpen
                        ? "Claim Locked"
                        : claimLoading || isClaiming
                        ? "Claiming..."
                        : "Claim AIFL"}
                    </span>
                    <div className="corner" data-colors="primary">
                      <span className="corner-left"></span>
                      <span className="corner-right"></span>
                      <span className="corner-top-bottom"></span>
                    </div>
                  </button>
                  <div className="corner" data-colors="tertiary">
                    <span className="corner-left"></span>
                    <span className="corner-right"></span>
                    <span className="corner-top-bottom"></span>
                  </div>
                </div>
                <div className="more">
                  <div className="more-arrow">
                    <div className="more-arrow-shape"></div>
                  </div>
                  <a href="#bottom" className="sub">
                    More infos
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </>
  );
};

export default Dashboard;
